import { memo, useCallback, useMemo, useState } from 'react';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import { DateTime } from 'luxon';
import { FormikErrors, FormikTouched } from 'formik';
import { useParams } from 'react-router-dom';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiButton } from 'shell/components/shared/button';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { AnprVM } from 'shell/view-models/search-anpr';
import { PersonVM } from 'shell/view-models/search-person';
import { transformerPersonList } from 'shell/utils/transform-person-list';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import { ItemVM } from 'shell/view-models/item';
import { noop } from 'shell/utils/noop';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

export enum TipoProprietario {
  PersonaFisica = 'F',
  PersonaGiuridica = 'G',
}

const headCells: HeadCell<PersonVM, keyof PersonVM>[] = [
  { id: 'fiscalCode', label: 'Codice Fiscale' },
  { id: 'surname', label: 'Cognome' },
  { id: 'name', label: 'Nome' },
  { id: 'gender', label: 'Sesso' },
  { id: 'birthday', label: 'Data di nascita' },
  { id: 'birthplace', label: 'Luogo di nascita' },
  { id: 'residence', label: 'Residenza' },
  { id: 'state', label: 'Stato' },
  { id: 'anpr', label: 'ANPR' },
  { id: 'action', label: 'Azione' },
];

interface DatiProprietarioProps {
  values?: ItalianVM;
  onChange: (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => void;
  onChanges: (values: ItalianVM) => void;
  errors?: FormikErrors<ItalianVM>;
  touched?: FormikTouched<ItalianVM>;
  disabled?: boolean;
}

const DatiProprietarioComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  touched,
  disabled,
}: DatiProprietarioProps): React.ReactElement => {
  const params = useParams();

  const tipoProprietario = values?.tipoProprietario || TipoProprietario.PersonaFisica;

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [visibilityTable, setVisibilityTable] = useState(false);

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<PersonVM>>(
    `${__PERSON_NEW_API_URL__}/ricerca-persona/proprietario`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonList,
    }
  );

  const castedAnprData = anpr.data as AnprVM<PersonVM>;

  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // La ricerca ANPR è abilitata solo per Persona fisica con CF oppure Nome + Cognome
  const isSearchEnabled = useMemo(() => {
    if (tipoProprietario !== TipoProprietario.PersonaFisica) {
      return false;
    }
    return Boolean(
      values?.fiscalCodeProprietario || (values?.nameProprietario && values?.surnameProprietario)
    );
  }, [
    tipoProprietario,
    values?.fiscalCodeProprietario,
    values?.nameProprietario,
    values?.surnameProprietario,
  ]);

  const populateFromPerson = useEventCallback((person: PersonVM) => {
    onChanges({
      fiscalCodeProprietario: person.fiscalCode || '',
      surnameProprietario: person.surname || '',
      nameProprietario: person.name || '',
      municipalityResidenceProprietario: person.residence || '',
      addressProprietario: person.address || '',
      dataNascitaProprietario: person.generality?.dataNascita
        ? DateTime.fromISO(person.generality.dataNascita)
        : values?.dataNascitaProprietario || null,
    });
  });

  const handleSearchProprietario = useEventCallback(() => {
    setVisibilityTable(false);

    if (!isSearchEnabled) {
      return;
    }

    (
      getAnpr({
        data: {
          codiceEnte: params.codiceEnte,
          tipoRegistro: params.tipoRegistro,
          idAnnoPratica: Number(params.idAnnoPratica),
          progressivoPratica: Number(params.progressivoPratica),
          codFiscale: values?.fiscalCodeProprietario || undefined,
          cognome: values?.surnameProprietario || undefined,
          nome: values?.nameProprietario || undefined,
          dataNascita: values?.dataNascitaProprietario?.toFormat('yyyy-MM-dd') || undefined,
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<PersonVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<PersonVM>;

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse.persons;
        populateFromPerson(element);
        setVisibilityTable(false);
        resetPagination();
        setVisibility({});
        return;
      }
      // 0 o più risultati: mostra tabella / messaggi (il warning 9007 arriva su warningInformation)
      resetPagination();
      setVisibilityTable(true);
    });
  });

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? undefined : open;
      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const handleClickSelect = useEventCallback((person: PersonVM) => {
    populateFromPerson(person);
    setVisibilityTable(false);
    resetPagination();
    setVisibility({});
  });

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        ...person,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  const warningMessages = useMemo<Array<ItemVM>>(() => {
    const messages = [...(castedAnprData?.warnings || [])];
    if (castedAnprData?.warningInformation) {
      messages.push({ id: 'warning-info', value: castedAnprData.warningInformation });
    }
    return messages;
  }, [castedAnprData?.warnings, castedAnprData?.warningInformation]);

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const onChangeTipoProprietario = useEventCallback((value: string) => {
    if (value === TipoProprietario.PersonaGiuridica) {
      onChanges({
        tipoProprietario: TipoProprietario.PersonaGiuridica,
        dataNascitaProprietario: null,
        fiscalCodeProprietario: '',
        addressProprietario: '',
        surnameProprietario: '',
        nameProprietario: '',
        municipalityResidenceProprietario: '',
      });
      return;
    }
    onChange('tipoProprietario', TipoProprietario.PersonaFisica);
  });

  const isPersonaFisica = tipoProprietario === TipoProprietario.PersonaFisica;

  return (
    <>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati Proprietario/Locatore</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {visibilityTable || errorMessage.visibility ? (
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={
              warningMessages.length > 1
                ? `Ci sono ${warningMessages.length} warning`
                : `C'è 1 warning`
            }
            messages={warningMessages}
            visibility={
              visibility.warning !== undefined ? visibility.warning : warningMessages.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <FormControl component="fieldset" disabled={disabled}>
          <RadioGroup
            row
            aria-label="Tipo proprietario"
            id="tipoProprietario"
            value={tipoProprietario}
            onChange={(event) => onChangeTipoProprietario(event.target.value)}
          >
            <FormControlLabel
              value={TipoProprietario.PersonaFisica}
              control={<Radio />}
              label="Persona fisica"
            />
            <FormControlLabel
              value={TipoProprietario.PersonaGiuridica}
              control={<Radio />}
              label="Persona giuridica"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          id="fiscalCodeProprietario"
          disabled={disabled}
          label={isPersonaFisica ? 'Codice Fiscale' : 'Partita IVA'}
          aria-label={isPersonaFisica ? 'Codice Fiscale' : 'Partita IVA'}
          fullWidth
          helperText={errors?.fiscalCodeProprietario}
          error={touched?.fiscalCodeProprietario && Boolean(errors?.fiscalCodeProprietario)}
          value={values?.fiscalCodeProprietario || ''}
          onChange={(event) => onChange('fiscalCodeProprietario', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          id="surnameProprietario"
          label={isPersonaFisica ? 'Cognome' : 'Ragione Sociale'}
          aria-label={isPersonaFisica ? 'Cognome' : 'Ragione Sociale'}
          disabled={disabled}
          fullWidth
          helperText={errors?.surnameProprietario}
          error={touched?.surnameProprietario && Boolean(errors?.surnameProprietario)}
          value={values?.surnameProprietario || ''}
          onChange={(event) => onChange('surnameProprietario', event.target.value)}
        />
      </Grid>
      {isPersonaFisica ? (
        <>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="nameProprietario"
              label="Nome"
              aria-label="Nome"
              disabled={disabled}
              fullWidth
              helperText={errors?.nameProprietario}
              error={touched?.nameProprietario && Boolean(errors?.nameProprietario)}
              value={values?.nameProprietario || ''}
              onChange={(event) => onChange('nameProprietario', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data di nascita"
              aria-label="Data di nascita"
              value={values?.dataNascitaProprietario || null}
              disabled={disabled}
              onChange={(date) => onChange('dataNascitaProprietario', date)}
              renderInput={(pickerParams) => (
                <PmiTextField
                  {...pickerParams}
                  id="dataNascitaProprietario"
                  fullWidth
                  helperText={errors?.dataNascitaProprietario as string}
                  error={
                    touched?.dataNascitaProprietario && Boolean(errors?.dataNascitaProprietario)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignSelf="center">
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              disabled={disabled || !isSearchEnabled}
              onClick={handleSearchProprietario}
            >
              Cerca
            </PmiButton>
          </Grid>
        </>
      ) : undefined}
      {isPersonaFisica ? (
        <Grid item xs={12} md={6}>
          <PmiTextField
            id="municipalityResidenceProprietario"
            label="Comune Residenza"
            disabled={disabled}
            fullWidth
            aria-label="Comune Residenza"
            error={
              touched?.municipalityResidenceProprietario &&
              Boolean(errors?.municipalityResidenceProprietario)
            }
            value={values?.municipalityResidenceProprietario || ''}
            onChange={(event) => onChange('municipalityResidenceProprietario', event.target.value)}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={6}>
        <PmiTextField
          id="addressProprietario"
          label="Indirizzo"
          aria-label="Indirizzo"
          disabled={disabled}
          fullWidth
          helperText={errors?.addressProprietario}
          error={touched?.addressProprietario && Boolean(errors?.addressProprietario)}
          value={values?.addressProprietario || ''}
          onChange={(event) => onChange('addressProprietario', event.target.value)}
        />
      </Grid>
      {dataPersons.length > 1 && visibilityTable ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista Proprietari"
            page={paginationState.page}
            rowsPerPage={paginationState.rowsPerPage}
            body={dataPersons}
            count={dataPersons.length}
            head={headCells}
            onSort={noop}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onVisibilityPress={noop}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        </Grid>
      ) : undefined}
    </>
  );
};

DatiProprietarioComponent.displayName = 'Dati Proprietario';

const DatiProprietario = memo(DatiProprietarioComponent);

export default DatiProprietario;
